import apiService from './api';

export type ShiftType = 'DAY' | 'NIGHT';
export type AttendanceStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

export interface AttendanceEntry {
  id: number;
  employeeId: number;
  shiftType: ShiftType;
  clockInAt: string;
  clockOutAt: string | null;
  status: AttendanceStatus;
  approvedBy: number | null;
  approvedAt: string | null;
  rejectReason: string | null;
  tenantId?: number | null;
  employee?: {
    id: number;
    userId: number;
    designation?: string | null;
    user?: { id: number; name: string; email: string };
  };
  approver?: { id: number; name: string; email: string } | null;
  createdAt: string;
  updatedAt: string;
}

export interface RecordsFilter {
  employeeId?: number;
  status?: AttendanceStatus;
  fromDate?: string;
  toDate?: string;
}

// --- Extra-work requests (karigar salary extras: machines or hours) ---
export type ExtraMachineStatus = 'PENDING' | 'APPROVED' | 'REJECTED';
export type ExtraUnit = 'MACHINE' | 'HOUR';
export type PayType = 'FIXED' | 'METER';

export interface ExtraMachineRequest {
  id: number;
  employeeId: number;
  workDate: string;        // YYYY-MM-DD
  quantity: number;        // machines or hours (see unit)
  unit: ExtraUnit;
  note: string | null;
  status: ExtraMachineStatus;
  approvedBy: number | null;
  approvedAt: string | null;
  rejectReason: string | null;
  tenantId?: number | null;
  employee?: {
    id: number;
    userId: number;
    designation?: string | null;
    user?: { id: number; name: string; email: string };
  };
  approver?: { id: number; name: string; email: string } | null;
  createdAt: string;
  updatedAt: string;
}

// --- Monthly summary (calendar + present/absent + salary) ---
export interface SummaryDay {
  date: string;            // YYYY-MM-DD
  present: boolean;
  shifts?: ShiftType[];
  hours?: number;          // admin-only (omitted in karigar self-view)
}

export interface MonthlySummary {
  employeeId: number;
  employeeName: string | null;
  designation: string | null;
  month: string;           // YYYY-MM
  daysInMonth: number;
  presentDays: number;
  absentDays: number;
  days: SummaryDay[];
  payType: PayType;
  monthlyRate?: number | null; // FIXED: full monthly rate before attendance proration
  basePay: number;         // FIXED: (monthlyRate ÷ 30) × present days; METER: meters × shift rate
  meters: number;          // total meters (METER only; 0 for FIXED)
  extraUnit: ExtraUnit;
  extraRate: number;       // ₹ per machine or per hour
  extraQuantity: number;   // approved machines/hours this month
  extrasAmount: number;    // extraQuantity × extraRate
  computedSalary: number;  // basePay + extrasAmount
  workingHours?: number;   // admin-only
}

const attendanceApi = {
  // --- Employee self-service ---
  clockIn: async (shiftType: ShiftType): Promise<{ entry: AttendanceEntry; message: string }> =>
    apiService.post('/attendance/clock-in', { shiftType }),

  clockOut: async (): Promise<{ entry: AttendanceEntry; message: string }> =>
    apiService.post('/attendance/clock-out', {}),

  getMyCurrent: async (): Promise<{ current: AttendanceEntry | null }> =>
    apiService.get('/attendance/me/current'),

  getMyHistory: async (fromDate?: string, toDate?: string): Promise<{ entries: AttendanceEntry[] }> => {
    const params = new URLSearchParams();
    if (fromDate) params.append('fromDate', fromDate);
    if (toDate) params.append('toDate', toDate);
    const qs = params.toString();
    return apiService.get(`/attendance/me${qs ? `?${qs}` : ''}`);
  },

  // --- Approver / admin ---
  getPending: async (): Promise<{ entries: AttendanceEntry[] }> =>
    apiService.get('/attendance/pending'),

  approve: async (id: number): Promise<{ entry: AttendanceEntry; message: string }> =>
    apiService.post(`/attendance/${id}/approve`, {}),

  reject: async (id: number, reason?: string): Promise<{ entry: AttendanceEntry; message: string }> =>
    apiService.post(`/attendance/${id}/reject`, { reason }),

  getRecords: async (filter: RecordsFilter = {}): Promise<{ entries: AttendanceEntry[] }> => {
    const params = new URLSearchParams();
    if (filter.employeeId) params.append('employeeId', String(filter.employeeId));
    if (filter.status) params.append('status', filter.status);
    if (filter.fromDate) params.append('fromDate', filter.fromDate);
    if (filter.toDate) params.append('toDate', filter.toDate);
    const qs = params.toString();
    return apiService.get(`/attendance/records${qs ? `?${qs}` : ''}`);
  },

  // --- Monthly summary ---
  getSummary: async (employeeId: number, month: string): Promise<{ summary: MonthlySummary }> =>
    apiService.get(`/attendance/summary?employeeId=${employeeId}&month=${month}`),

  getMySummary: async (month: string): Promise<{ summary: MonthlySummary }> =>
    apiService.get(`/attendance/me/summary?month=${month}`),
};

// Extra-machine request API (mirrors the attendance approval flow).
export const extraMachineApi = {
  // Karigar self-service
  create: async (payload: { workDate: string; quantity: number; note?: string }): Promise<{ request: ExtraMachineRequest; message: string }> =>
    apiService.post('/extra-machines', payload),

  getMine: async (month?: string): Promise<{ requests: ExtraMachineRequest[] }> =>
    apiService.get(`/extra-machines/me${month ? `?month=${month}` : ''}`),

  // Approver / admin
  getPending: async (): Promise<{ requests: ExtraMachineRequest[] }> =>
    apiService.get('/extra-machines/pending'),

  approve: async (id: number): Promise<{ request: ExtraMachineRequest; message: string }> =>
    apiService.post(`/extra-machines/${id}/approve`, {}),

  reject: async (id: number, reason?: string): Promise<{ request: ExtraMachineRequest; message: string }> =>
    apiService.post(`/extra-machines/${id}/reject`, { reason }),

  getRecords: async (filter: { employeeId?: number; status?: ExtraMachineStatus; month?: string } = {}): Promise<{ requests: ExtraMachineRequest[] }> => {
    const params = new URLSearchParams();
    if (filter.employeeId) params.append('employeeId', String(filter.employeeId));
    if (filter.status) params.append('status', filter.status);
    if (filter.month) params.append('month', filter.month);
    const qs = params.toString();
    return apiService.get(`/extra-machines${qs ? `?${qs}` : ''}`);
  },
};

export default attendanceApi;
